import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';
import { OperationArchivesContainer, SearchSchema } from '@/features/operation-archives';
import { useInHousePackageOptions } from '../in-house-packages/-query/api';
import { useItOperationPackages } from '../packages/-query/api';
import { useItOperationCoreArchives } from './-query';
export const Route = createFileRoute('/_staff/it-operation-core/_root/archives/')({
  component: ItOperationCoreArchives,
  validateSearch: zodValidator(SearchSchema)
});
function ItOperationCoreArchives() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const archivesQuery = useItOperationCoreArchives({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    textSearch: search.textSearch,
    operationPackageId: search.operationPackageId,
    anchorDateFrom: search.anchorDateFrom,
    anchorDateTo: search.anchorDateTo,
    orderBy: search.orderBy,
    orderDirection: search.orderDirection
  });
  const {
    data: operationPackagesData,
    isPending: isOperationPackagesPending
  } = useItOperationPackages();

  // テナント専用パッケージ（in-house）は grouping 関数で破棄されるため別途取得してフィルター選択肢に合流させる
  const {
    data: inHousePackageOptions,
    isPending: isInHousePackageOptionsPending
  } = useInHousePackageOptions();
  const packageOptionsQuery = useMemo(() => ({
    // 標準パッケージ・in-house のどちらか一方でも取得できていれば選択肢を表示する
    // （片方が失敗・未取得でも、もう片方の選択肢を黙って欠落させない）
    data: operationPackagesData || inHousePackageOptions ? [...(operationPackagesData?.apps.map(item => ({
      id: item.id,
      name: item.name
    })) ?? []), ...(operationPackagesData?.devices.map(item => ({
      id: item.id,
      name: item.name
    })) ?? []), ...(operationPackagesData?.sharedDrive.map(item => ({
      id: item.id,
      name: item.name
    })) ?? []), ...(operationPackagesData?.network.map(item => ({
      id: item.id,
      name: item.name
    })) ?? []), ...(inHousePackageOptions ?? [])] : undefined,
    isPending: isOperationPackagesPending || isInHousePackageOptionsPending
  }), [operationPackagesData, isOperationPackagesPending, inHousePackageOptions, isInHousePackageOptionsPending]);

  /**
   * アーカイブ行クリック時の遷移処理（オペレーションインスタンス詳細画面へ）
   *
   * 以前はデバイス棚卸しだけ旧 `/device-inventory/data` へ分岐していたが、BE が
   * `{requestId}/progress` `{requestId}/summary` `/self-reports` を撤去して同画面が
   * 動かなくなったため削除した。
   *
   * ⚠️ この「動かないから外した」という理由は #4488 で失効している。v1 画面も上記
   * REST も凍結レガシーとして復元済み。それでも現状は世代を問わずインスタンス詳細へ
   * 寄せたままにしている（v2 の束ね画面 `/device-inventory-confirmation` は `tagId`
   * 必須でアーカイブ行から接続できない、という当時の事情は v2 側にはそのまま残る）。
   * v1 パターンのアーカイブだけ旧データ画面へ戻すかどうかは製品判断で未決。
   * 技術的には行が持つ `patternId` / `instanceId` を
   * `?patternId=&packageInstanceId=` に渡せば接続できる。
   */
  const handleArchiveRowClick = useCallback((row: {
    packageType: string;
    patternId: string;
    instanceId: string;
  }) => {
    navigate({
      to: `/it-operation-core/operation-instances/${row.instanceId}/detail`
    });
  }, [navigate]);
  return <OperationArchivesContainer searchParams={search} onNavigate={navigate} archivesQuery={archivesQuery} packageOptionsQuery={packageOptionsQuery} onRowClick={handleArchiveRowClick} hideTargetForPackageTypes={['BulkAppUserCreation', 'BulkAppUserDeactivation', 'DeviceInventoryLabelCreation']} />;
}